import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import { NumericalCellValueStyle } from '@helpers/utils/table/table';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useThemePerformanceDisplay } from '@terminal/hooks/use-theme-performance-display/useThemePerformanceDisplay';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { ThemeLogoIcon } from '@terminal/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { formatPercentageNumber } from '@terminal/widgets/earnings/utils/utils';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { CatalystBadge } from '../catalyst-badge/CatalystBadge';
import styles from './ThematicCatalystBadge.module.scss';

const numericalStyleToClass: Record<NumericalCellValueStyle, string> = {
  [NumericalCellValueStyle.Positive]: semanticColorStyles.directionPositive,
  [NumericalCellValueStyle.Negative]: semanticColorStyles.directionNegative,
  [NumericalCellValueStyle.Neutral]:
    semanticColorStyles.directionNeutralDefault,
};

export type ThematicCatalystBadgeProps = {
  themeId?: string;
  source?: PosthogTrackingSource;
};

export const ThematicCatalystBadge = ({
  themeId,
  source = POSTHOG_TRACKING_SOURCES.CATALYST_INSIGHT_DRAWER,
}: ThematicCatalystBadgeProps) => {
  const { t } = useTranslation(['widget']);
  const { data: themeData } = useTheme({ themeId });
  const { navigateToThemeOverview } = useNavigateToThemeOverview();
  const { displayValue, numericalStyle } = useThemePerformanceDisplay(themeId);

  if (!themeData || !themeId) {
    return null;
  }

  return (
    <CatalystBadge
      tooltipLabel={t('widget:assetOverview.openThemeOverview')}
      onClick={() => navigateToThemeOverview(themeId, source)}
      logo={
        <ThemeLogoIcon
          logo_url={themeData.logo_url}
          name={themeData.name}
          type={themeData.type}
          size={16}
        />
      }
      name={themeData.name}
      trailing={
        displayValue !== undefined ? (
          <span
            className={clsx(
              styles.performance,
              numericalStyleToClass[numericalStyle]
            )}
          >
            {formatPercentageNumber({
              value: displayValue,
              includePercentSign: true,
              addPlus: true,
              formatZero: true,
            })}
          </span>
        ) : undefined
      }
    />
  );
};
